/** Pure parts of the Live Data Status page: words, ages, sorting, grouping. */

import type { components } from '../../api/generated/schema.ts';

export type LiveStatus = components['schemas']['LiveStatus'];
export type Ship = components['schemas']['LiveStatusShip'];
export type Stream = components['schemas']['LiveStatusSection'];
export type Verdict = Stream['status'];
export type Sort = { key: string; dir: 'asc' | 'desc' } | null;
export type Filter = 'all' | Verdict;
export type LiveSessions = components['schemas']['LiveSessions'];
export type LiveSession = components['schemas']['LiveSession'];

/** MAN_OUT -> "Man out". EOS Live has no label list for its session statuses, so the code becomes the words. */
export function sessionStatusWord(code: string | null | undefined): string {
  if (!code) return 'Unknown';
  const words = code.toLowerCase().replace(/_/g, ' ').replace(/\baaqs\b/, 'AAQS');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The Live API's sections, in column order, with the words people read. */
export const STREAMS: ReadonlyArray<readonly [key: string, label: string]> = [
  ['nautical', 'Nautical'],
  ['engine', 'Engine'],
  ['energy', 'Energy and propulsion'],
  ['aaqs', 'AAQS'],
];
export const WORD: Record<Verdict, string> = {
  ok: 'OK',
  warning: 'Late',
  critical: 'Critical',
  missing: 'Missing',
};
/** Streams a ship may not be fitted with. The Live API leaves them out; the cell says "No AAQS". */
export const OPTIONAL: Record<string, string> = { aaqs: 'AAQS' };
export const RANK: Record<Verdict, number> = { ok: 0, warning: 1, critical: 2, missing: 3 };

/** Redis field names that are not the Live column name lowercased. */
export const NAUTICAL_COLUMNS: Record<string, string> = {
  SpeedOverGround: 'sog',
  SpeedThroughWater: 'stw',
};
export const WINDOWS = ['1h', '2h', '3h', '6h', '12h', '24h', '48h'] as const;

/** The house cycle (`nextSort` in render-table.ts): ascending, descending, off. */
export function nextSort(current: Sort, key: string): Sort {
  if (!current || current.key !== key) return { key, dir: 'asc' };
  return current.dir === 'asc' ? { key, dir: 'desc' } : null;
}

export function formatAge(s: number | null | undefined): string {
  if (s == null) return '-';
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ${m % 60} min`;
  return `${Math.floor(h / 24)} d`;
}

/** A late stream shows its oldest late reading; an OK one its newest. A frozen reading is not late. */
export function shownAge(st: Stream): number | null {
  const late = Object.values(st.signals).filter((x) => x.status !== 'ok' && !x.frozen && x.age_seconds != null);
  if (st.status === 'ok' || st.heartbeat || !late.length) return st.age_seconds ?? null;
  return Math.max(...late.map((x) => x.age_seconds as number));
}

/** Signals that reported one value only over the window: stuck sensors (Live API 1.4). */
export function frozenSignals(st: Stream): string[] {
  return Object.entries(st.signals)
    .filter(([, x]) => x.frozen)
    .map(([name]) => name);
}

/** The cell's word when frozen values are the stream's only trouble; a late reading says more. */
export function frozenLabel(st: Stream): string | null {
  const n = frozenSignals(st).length;
  if (!n || Object.values(st.signals).some((x) => !x.frozen && x.status !== 'ok')) return null;
  return n === 1 ? 'Frozen' : `${n} frozen`;
}

/** The cell's text: Missing, the frozen word, or the age plus how many signals are late or frozen. */
export function cellText(st: Stream, age: (s: number | null) => string): string {
  if (st.status === 'missing') return WORD.missing;
  const frozen = frozenLabel(st);
  if (frozen) return frozen;
  const n = Object.values(st.signals).filter((x) => x.frozen || x.status !== 'ok').length;
  return n ? `${age(shownAge(st))} · ${n} signal${n === 1 ? '' : 's'}` : age(shownAge(st));
}

/** Ships in brand order, then sorted inside each brand. */
export function groupRows(
  ships: Array<[string, Ship]>,
  sort: Sort,
  label: (id: string) => string,
  brand: (id: string) => string,
): Array<[string, Array<[string, Ship]>]> {
  const byName = (a: [string, Ship], b: [string, Ship]) => label(a[0]).localeCompare(label(b[0]));
  const cmp = (a: [string, Ship], b: [string, Ship]): number => {
    if (!sort) return byName(a, b);
    const sign = sort.dir === 'asc' ? 1 : -1;
    if (sort.key === 'name') return sign * byName(a, b);
    const x = a[1].sections[sort.key], y = b[1].sections[sort.key];
    // A stream the ship is not fitted with counts as better than OK.
    if (!x || !y) return sign * ((x ? -1 : 0) + (y ? 1 : 0)) || byName(a, b);
    // Ascending on a stream is worst first: the reason to sort by it.
    const worse = RANK[y.status] - RANK[x.status] || (shownAge(y) ?? 1e12) - (shownAge(x) ?? 1e12);
    return sign * worse || byName(a, b);
  };
  const groups = new Map<string, Array<[string, Ship]>>();
  for (const row of ships) {
    const b = brand(row[0]) || 'Other';
    groups.set(b, [...(groups.get(b) ?? []), row]);
  }
  return [...groups]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([b, rows]) => [b, rows.sort(cmp)]);
}

/** One line under a ship in the PDF's "Needs attention": a late or frozen signal, or a whole stream. */
export type AttentionRow = { mark: Verdict | 'frozen'; name: string; value: string };

export interface Attention {
  id: string;
  status: Verdict;
  /** Labels of the streams that are not OK. */
  streams: string[];
  /** "3 late · 11 frozen". */
  summary: string;
  late: AttentionRow[];
  frozen: AttentionRow[];
}

/** Every ship that is not OK, worst first, with its late and frozen signals. */
export function attentionList(ships: Array<[string, Ship]>, label: (id: string) => string): Attention[] {
  const out: Attention[] = [];
  for (const [id, ship] of ships) {
    const late: AttentionRow[] = [];
    const frozen: AttentionRow[] = [];
    const streams: string[] = [];
    for (const [key, name] of STREAMS) {
      const st = ship.sections[key];
      if (!st || st.status === 'ok') continue;
      streams.push(name);
      const signals = Object.entries(st.signals).sort(([, a], [, b]) => (b.age_seconds ?? 0) - (a.age_seconds ?? 0));
      if (st.status === 'missing') late.push({ mark: 'missing', name: `${name}: no reading`, value: WORD.missing });
      else if (st.heartbeat || !signals.length)
        late.push({ mark: st.status, name: `${name}: newest reading`, value: formatAge(st.age_seconds) });
      for (const [sig, s] of st.status === 'missing' ? [] : signals) {
        if (s.frozen) frozen.push({ mark: 'frozen', name: sig, value: `frozen at ${s.frozen.value}` });
        else if (s.status !== 'ok') late.push({ mark: s.status, name: sig, value: formatAge(s.age_seconds) });
      }
    }
    if (!streams.length) continue;
    const missing = late.filter((r) => r.mark === 'missing').length;
    const parts = [
      late.length - missing && `${late.length - missing} late`,
      missing && `${missing} missing`,
      frozen.length && `${frozen.length} frozen`,
    ].filter(Boolean);
    out.push({ id, status: ship.status, streams, summary: parts.join(' · '), late, frozen });
  }
  const weight = (a: Attention) => a.late.length + a.frozen.length;
  return out.sort(
    (a, b) => RANK[b.status] - RANK[a.status] || weight(b) - weight(a) || label(a.id).localeCompare(label(b.id)),
  );
}
